import { useRef, useState } from "react";
import {
  useCreateErpExport,
  useDeleteErpBatch,
  useDeleteErpBatchesBefore,
  useDownloadErpBatch,
  useDownloadErpRange,
  useErpExportBatches,
  useErpExportPreview,
  useErpExportStatus,
  useErpInvoicePdf,
  useErpInvoices,
  useNumberNow,
  useReexportBatch,
  useRectifyInvoice,
  useRefundWarnings,
  useTenants,
  type ErpExportBatch,
  type ErpInvoiceFilters,
  type ErpInvoiceRow,
} from "../api/hooks";
import { ApiError } from "../api/client";
import { Button, Card, Chip, ErrorNotice, Field, Input, Notice, PageHeader, Select, Spinner } from "../components/ui";
import {
  batchFilename,
  describeBlockReason,
  describeInvoicePage,
  formatDay,
  formatEuro,
  groupProblems,
  neighbourOffsets,
  previousMonthRange,
} from "../lib/erp-export";
import { fiscalPdfFilename, openBlankTab, showPdfBlob } from "../lib/pdf-open";

/**
 * Exportación de facturas al ERP. Solo super-admin.
 *
 * Solo entran facturas ya NUMERADAS. Un lote marca sus facturas como exportadas; reexportar genera el fichero
 * otra vez sin cambiar esas marcas. Si algún dato no cabe en el ERP, la exportación se bloquea y se explica
 * qué corregir: nunca se recorta nada.
 */

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
}

const DEFAULT_INVOICE_FILTERS: ErpInvoiceFilters = {
  from: "",
  to: "",
  tenantId: "",
  batchId: "",
  exported: "all",
  test: "include",
  limit: 50,
  offset: 0,
};

export function ErpExportPage() {
  const initial = previousMonthRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [allowGap, setAllowGap] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);

  const status = useErpExportStatus();
  const preview = useErpExportPreview(range);
  const batches = useErpExportBatches();
  const create = useCreateErpExport();
  const reexport = useReexportBatch();
  const download = useDownloadErpBatch();
  const numberNow = useNumberNow();
  const deleteBatch = useDeleteErpBatch();
  const deleteBefore = useDeleteErpBatchesBefore();
  const downloadRange = useDownloadErpRange();
  const rectify = useRectifyInvoice();
  const invoicePdf = useErpInvoicePdf();
  const refundWarnings = useRefundWarnings();
  const [purgeBefore, setPurgeBefore] = useState("");
  const [rangeFrom, setRangeFrom] = useState(initial.from);
  const [rangeTo, setRangeTo] = useState(initial.to);
  const reRangeInvalid = rangeFrom === "" || rangeTo === "" || rangeFrom > rangeTo;

  const [filters, setFilters] = useState<ErpInvoiceFilters>(DEFAULT_INVOICE_FILTERS);
  const invoices = useErpInvoices(filters);
  const tenants = useTenants();
  const listRef = useRef<HTMLDivElement | null>(null);

  function changeFilters(patch: Partial<ErpInvoiceFilters>): void {
    // Cualquier cambio de filtro vuelve a la primera página; solo cambiar de página conserva el resto.
    setFilters((current) => ({ ...current, offset: 0, ...patch }));
  }

  function showBatchInvoices(batch: ErpExportBatch): void {
    setFilters({ ...DEFAULT_INVOICE_FILTERS, batchId: batch.id });
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const rangeInvalid = from === "" || to === "" || from > to;
  const data = preview.data;
  const hasGap = (data?.gap.count ?? 0) > 0;

  async function downloadBatch(batch: ErpExportBatch): Promise<void> {
    try {
      saveBlob(await download.mutateAsync(batch.id), batchFilename(batch));
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo descargar" });
    }
  }

  async function handleCreate(): Promise<void> {
    if (!range) return;
    setMessage(null);
    try {
      const batch = await create.mutateAsync({ ...range, allowGap });
      setAllowGap(false);
      setMessage({
        tone: "ok",
        text: `Lote creado: ${batch.invoiceCount} facturas por ${formatEuro(batch.totalAmount)}. Descargando el fichero…`,
      });
      await downloadBatch(batch);
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo crear la exportación" });
    }
  }

  async function handleReexport(batch: ErpExportBatch): Promise<void> {
    setMessage(null);
    try {
      const created = await reexport.mutateAsync(batch.id);
      setMessage({ tone: "ok", text: `Reexportación creada (${created.invoiceCount} facturas). Descargando el fichero…` });
      await downloadBatch(created);
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo reexportar" });
    }
  }

  async function handleDeleteBatch(batch: ErpExportBatch): Promise<void> {
    const what = batch.isReexport ? "esta reexportación" : "esta exportación y sus reexportaciones";
    const ok = window.confirm(
      `¿Borrar ${what} del historial (${formatDay(batch.from)} – ${formatDay(batch.to)})?\n\n` +
        "Las facturas conservan su número y su marca de exportada: no volverán a salir en una exportación nueva. " +
        "Podrás volver a descargarlas desde «Descargar por fechas».",
    );
    if (!ok) return;
    setMessage(null);
    try {
      const result = await deleteBatch.mutateAsync(batch.id);
      setMessage({
        tone: "ok",
        text: `Historial borrado: ${result.deleted} registro(s). Las ${result.invoicesKept} factura(s) afectadas conservan número y marca de exportada.`,
      });
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo borrar" });
    }
  }

  async function handleDeleteBefore(): Promise<void> {
    if (purgeBefore === "") return;
    const ok = window.confirm(
      `¿Borrar del historial todas las exportaciones creadas antes del ${formatDay(purgeBefore)}?\n\n` +
        "Las facturas conservan su número y su marca de exportada; se podrán descargar por fechas.",
    );
    if (!ok) return;
    setMessage(null);
    try {
      const result = await deleteBefore.mutateAsync(purgeBefore);
      setMessage({
        tone: "ok",
        text:
          result.deleted === 0
            ? "No había exportaciones anteriores a esa fecha."
            : `Historial borrado: ${result.deleted} registro(s). Las ${result.invoicesKept} factura(s) afectadas conservan número y marca de exportada.`,
      });
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo borrar" });
    }
  }

  async function handleDownloadRange(): Promise<void> {
    if (reRangeInvalid) return;
    setMessage(null);
    try {
      const blob = await downloadRange.mutateAsync({ from: rangeFrom, to: rangeTo });
      saveBlob(blob, `erp_${rangeFrom}_${rangeTo}_reexport.zip`);
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo descargar" });
    }
  }

  async function handleOpenPdf(invoice: ErpInvoiceRow): Promise<void> {
    setMessage(null);
    const tab = openBlankTab();
    try {
      const blob = await invoicePdf.mutateAsync(invoice.id);
      showPdfBlob(blob, tab, fiscalPdfFilename(invoice.reference, invoice.kind === "RECTIFICATIVE"));
    } catch (error) {
      tab?.close();
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo obtener la factura fiscal" });
    }
  }

  async function handleRectify(invoice: ErpInvoiceRow): Promise<void> {
    const ok = window.confirm(
      `¿Crear la rectificativa de la factura ${invoice.reference} (${formatEuro(invoice.totalAmount)})?\n\n` +
        "Se anula la factura completa: se crea una copia con importes negativos, con su propio número en la serie " +
        "de rectificativas y la fecha de hoy. No se puede deshacer.\n\n" +
        "Esto NO devuelve el dinero en Stripe: el reembolso hay que hacerlo aparte, en Stripe.",
    );
    if (!ok) return;
    setMessage(null);
    try {
      const created = await rectify.mutateAsync(invoice.id);
      setMessage({
        tone: "ok",
        text: `Rectificativa ${created.reference} creada para la factura ${invoice.reference}. Saldrá en la próxima exportación.`,
      });
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo rectificar" });
    }
  }

  async function handleNumberNow(ignoreDelay: boolean): Promise<void> {
    setMessage(null);
    try {
      const result = await numberNow.mutateAsync({ ignoreDelay });
      const stopped = result.stoppedAt ? ` Se detuvo: ${describeBlockReason(result.stoppedAt.reason)}.` : "";
      setMessage({
        tone: result.stoppedAt ? "danger" : "ok",
        text: `Numeradas: ${result.numbered}. Descartadas antes de numerar: ${result.dropped}.${stopped}`,
      });
      if (range) await preview.refetch();
    } catch (error) {
      setMessage({ tone: "danger", text: error instanceof Error ? error.message : "No se pudo numerar" });
    }
  }

  const problemGroups = groupProblems(data?.problems ?? []);
  const createError = create.error instanceof ApiError ? create.error : null;

  return (
    <div>
      <PageHeader
        eyebrow="Facturación"
        title="Exportación al ERP"
        description="Genera los ficheros de importación del ERP (facturas y líneas) con las facturas ya numeradas. Cada lote marca sus facturas como exportadas."
      />

      {status.isLoading ? <Spinner /> : null}
      {status.isError ? <ErrorNotice error={status.error} onRetry={() => void status.refetch()} /> : null}

      {status.data ? (
        <section aria-label="Estado de la numeración" className="mb-5 space-y-3">
          {status.data.numberingBlocked ? (
            <Notice tone="danger">
              <strong>La numeración está detenida.</strong> Factura {status.data.numberingBlocked.invoiceRef} de{" "}
              {status.data.numberingBlocked.tenantName}: {describeBlockReason(status.data.numberingBlocked.reason)}
              {status.data.numberingBlocked.detail ? ` (${status.data.numberingBlocked.detail})` : ""}. Mientras no se
              resuelva, las facturas siguientes no se numeran (se respeta el orden por fecha de cobro).
            </Notice>
          ) : null}
          {status.data.finalizationFailures.length > 0 ? (
            <Notice tone="warn">
              <strong>Stripe no pudo finalizar {status.data.finalizationFailures.length} factura(s)</strong> en los
              últimos 30 días:
              <ul className="mt-1 list-disc pl-5">
                {status.data.finalizationFailures.map((failure) => (
                  <li key={`${failure.providerInvoiceId ?? "x"}-${failure.at}`}>
                    {failure.tenantName} · {failure.providerInvoiceId ?? "sin id"} · {formatDateTime(failure.at)}
                    {failure.message ? ` — ${failure.message}` : ""}
                  </li>
                ))}
              </ul>
            </Notice>
          ) : null}
          <div className="flex flex-wrap items-center gap-3 text-sm text-ink-700 dark:text-ink-200">
            <Chip tone={status.data.pendingNumbering > 0 ? "warn" : "neutral"}>
              Pendientes de numerar: {status.data.pendingNumbering}
            </Chip>
            <Chip tone={status.data.pendingExport > 0 ? "warn" : "ok"}>
              Numeradas sin exportar: {status.data.pendingExport}
            </Chip>
            <Button onClick={() => void handleNumberNow(false)} disabled={numberNow.isPending}>
              Numerar ahora
            </Button>
            <Button
              onClick={() => {
                if (window.confirm("Numerar también las facturas cobradas hace menos de los días de espera. ¿Continuar?")) {
                  void handleNumberNow(true);
                }
              }}
              disabled={numberNow.isPending}
            >
              Numerar ahora sin esperar
            </Button>
          </div>
        </section>
      ) : null}

      {message ? (
        <div className="mb-4">
          <Notice tone={message.tone}>{message.text}</Notice>
        </div>
      ) : null}

      {refundWarnings.data && refundWarnings.data.warnings.length > 0 ? (
        <section className="mb-4" aria-label="Facturas devueltas en Stripe sin rectificativa">
          <Notice tone="warn">
            <p className="font-medium">
              Stripe da por devueltas {refundWarnings.data.warnings.length} factura(s) que aún no tienen rectificativa:
            </p>
            <ul className="mt-1 list-disc pl-5">
              {refundWarnings.data.warnings.map((warning) => (
                <li key={warning.invoiceId}>
                  <span className="num font-medium">{warning.reference}</span> · {warning.tenantName}
                  {warning.testMode ? " (prueba)" : ""} · {formatEuro(warning.totalAmount)} ·{" "}
                  {warning.total
                    ? "devolución total"
                    : `devuelto ${formatEuro(warning.amountRefunded)} (parcial: una rectificativa anula la factura completa)`}
                  {" · "}
                  {warning.total ? (
                    <button
                      type="button"
                      className="underline"
                      onClick={() => {
                        setFilters({ ...DEFAULT_INVOICE_FILTERS });
                        listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                    >
                      Buscarla en la lista para rectificar
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </Notice>
        </section>
      ) : null}
      {refundWarnings.data && !refundWarnings.data.configured ? null : refundWarnings.data && refundWarnings.data.failed > 0 ? (
        <div className="mb-4">
          <Notice>
            No se pudieron comprobar {refundWarnings.data.failed} factura(s) contra Stripe; vuelve a abrir la página más tarde.
          </Notice>
        </div>
      ) : null}

      <Card className="mb-5">
        <h2 className="mb-3 text-base font-semibold text-ink-900 dark:text-ink-50">Nueva exportación</h2>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Desde (fecha fiscal)" htmlFor="export-from">
            <Input id="export-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </Field>
          <Field label="Hasta (fecha fiscal)" htmlFor="export-to">
            <Input id="export-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
          <Button
            variant="primary"
            disabled={rangeInvalid}
            onClick={() => {
              setMessage(null);
              setAllowGap(false);
              create.reset();
              setRange({ from, to });
            }}
          >
            Comprobar
          </Button>
        </div>
        {rangeInvalid && from !== "" && to !== "" ? (
          <p role="alert" className="mt-2 text-xs font-medium text-signal-danger">
            La fecha inicial no puede ser posterior a la final.
          </p>
        ) : null}

        {range && preview.isLoading ? <Spinner label="Comprobando" /> : null}
        {range && preview.isError ? (
          <div className="mt-4">
            <ErrorNotice error={preview.error} onRetry={() => void preview.refetch()} />
          </div>
        ) : null}

        {data ? (
          <div className="mt-4 space-y-3" aria-label="Resultado de la comprobación">
            <p className="text-sm text-ink-800 dark:text-ink-100">
              Del {formatDay(data.from)} al {formatDay(data.to)}: <strong>{data.invoiceCount}</strong> factura(s)
              numerada(s) pendientes de exportar, por <strong>{formatEuro(data.totalAmount)}</strong>.
              {data.alreadyExportedInRange > 0
                ? ` Otras ${data.alreadyExportedInRange} de ese rango ya se exportaron (se pueden reexportar desde el historial).`
                : ""}
            </p>

            {data.invoiceCount === 0 ? <Notice>No hay facturas numeradas pendientes de exportar en ese rango.</Notice> : null}

            {hasGap ? (
              <Notice tone="warn">
                <strong>Hay {data.gap.count} factura(s) numerada(s) anteriores a este rango sin exportar</strong>
                {data.gap.oldestReference
                  ? ` (la más antigua, ${data.gap.oldestReference}, del ${formatDay(data.gap.oldestFiscalDate ?? "")})`
                  : ""}
                . Lo normal es ampliar el rango para incluirlas; exportar así dejaría un hueco en el ERP.
                <label className="mt-2 flex items-center gap-2">
                  <input type="checkbox" checked={allowGap} onChange={(event) => setAllowGap(event.target.checked)} />
                  Entiendo el hueco y quiero exportar igualmente
                </label>
              </Notice>
            ) : null}

            {problemGroups.length > 0 ? (
              <Notice tone="danger">
                <strong>El ERP no admite estos datos; hay que corregirlos antes de exportar</strong> (no se recorta
                nada automáticamente):
                <ul className="mt-1 list-disc pl-5">
                  {problemGroups.map((group) =>
                    group.items.map((problem) => (
                      <li key={`${group.invoiceId}-${problem.field}`}>
                        {problem.tenantName} · factura {problem.invoiceRef} · <em>{problem.field}</em>:{" "}
                        {problem.message}
                      </li>
                    )),
                  )}
                </ul>
              </Notice>
            ) : null}

            {createError ? (
              <Notice tone="danger">
                {createError.message}
              </Notice>
            ) : null}

            <Button
              variant="primary"
              disabled={!data.canExport || (hasGap && !allowGap) || create.isPending}
              onClick={() => void handleCreate()}
            >
              {create.isPending ? "Exportando…" : "Exportar y descargar"}
            </Button>
          </div>
        ) : null}
      </Card>

      <Card>
        <h2 className="mb-3 text-base font-semibold text-ink-900 dark:text-ink-50">Historial de exportaciones</h2>
        {batches.isLoading ? <Spinner /> : null}
        {batches.isError ? <ErrorNotice error={batches.error} onRetry={() => void batches.refetch()} /> : null}
        {batches.data && batches.data.length === 0 ? <p className="text-sm text-ink-500">Todavía no hay exportaciones.</p> : null}
        {batches.data && batches.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="micro-label">
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Rango fiscal</th>
                  <th className="py-2 pr-4">Facturas</th>
                  <th className="py-2 pr-4">Importe</th>
                  <th className="py-2 pr-4">Tipo</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {batches.data.map((batch) => (
                  <tr key={batch.id} className="border-t border-ink-200 dark:border-ink-800">
                    <td className="py-2 pr-4">{formatDateTime(batch.createdAt)}</td>
                    <td className="py-2 pr-4">
                      {formatDay(batch.from)} – {formatDay(batch.to)}
                    </td>
                    <td className="py-2 pr-4">
                      {batch.invoiceCount}
                      {batch.invoicesAvailable !== batch.invoiceCount ? (
                        <span className="block text-xs text-ink-500 dark:text-ink-400">
                          {batch.invoicesAvailable === 0 ? "ya no existen" : `${batch.invoicesAvailable} existen aún`}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4">{formatEuro(batch.totalAmount)}</td>
                    <td className="py-2 pr-4">
                      <Chip tone={batch.isReexport ? "neutral" : "ok"}>{batch.isReexport ? "Reexportación" : "Original"}</Chip>
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <Button onClick={() => showBatchInvoices(batch)} disabled={batch.invoicesAvailable === 0}>
                          Ver facturas
                        </Button>
                        <Button onClick={() => void downloadBatch(batch)} disabled={download.isPending || batch.invoicesAvailable === 0}>
                          Descargar
                        </Button>
                        <Button onClick={() => void handleReexport(batch)} disabled={reexport.isPending || batch.invoicesAvailable === 0}>
                          Reexportar
                        </Button>
                        <Button variant="danger" onClick={() => void handleDeleteBatch(batch)} disabled={deleteBatch.isPending}>
                          Borrar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
          El fichero se genera siempre con los datos y la tabla de conversión actuales; reexportar no cambia las
          marcas de «exportada» de las facturas. Borrar un registro del historial tampoco las cambia.
        </p>

        <div className="mt-5 grid gap-5 border-t border-ink-200 pt-4 dark:border-ink-800 md:grid-cols-2">
          <section aria-label="Descargar por fechas">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Descargar por fechas</h3>
            <p className="mb-2 text-xs text-ink-500 dark:text-ink-400">
              Genera el fichero con las facturas ya exportadas cuya fecha fiscal caiga en el rango, sin necesitar el
              historial. No cambia ninguna marca.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Desde" htmlFor="erp-range-from">
                <Input id="erp-range-from" type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} />
              </Field>
              <Field label="Hasta" htmlFor="erp-range-to">
                <Input id="erp-range-to" type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} />
              </Field>
              <Button onClick={() => void handleDownloadRange()} disabled={reRangeInvalid || downloadRange.isPending}>
                {downloadRange.isPending ? "Generando…" : "Descargar por fechas"}
              </Button>
            </div>
          </section>

          <section aria-label="Borrar historial anterior a una fecha">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Borrar historial anterior a una fecha</h3>
            <p className="mb-2 text-xs text-ink-500 dark:text-ink-400">
              Quita del historial las exportaciones creadas antes de ese día. Las facturas conservan número y marca de
              exportada. Queda constancia en el registro de auditoría.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Anteriores a" htmlFor="erp-purge-before">
                <Input id="erp-purge-before" type="date" value={purgeBefore} onChange={(e) => setPurgeBefore(e.target.value)} />
              </Field>
              <Button variant="danger" onClick={() => void handleDeleteBefore()} disabled={purgeBefore === "" || deleteBefore.isPending}>
                {deleteBefore.isPending ? "Borrando…" : "Borrar anteriores"}
              </Button>
            </div>
          </section>
        </div>
      </Card>

      <div ref={listRef} className="mt-5 scroll-mt-4">
        <Card>
          <h2 className="mb-1 text-base font-semibold text-ink-900 dark:text-ink-50">Facturas numeradas</h2>
          <p className="mb-3 text-xs text-ink-500 dark:text-ink-400">
            Todas las facturas con número fiscal, de la más reciente a la más antigua. Las del modo prueba de Stripe
            llevan su marca.
          </p>

          <div className="flex flex-wrap items-end gap-3" role="search" aria-label="Filtros de facturas">
            <Field label="Desde (fecha fiscal)" htmlFor="inv-from">
              <Input id="inv-from" type="date" value={filters.from} onChange={(event) => changeFilters({ from: event.target.value })} />
            </Field>
            <Field label="Hasta (fecha fiscal)" htmlFor="inv-to">
              <Input id="inv-to" type="date" value={filters.to} onChange={(event) => changeFilters({ to: event.target.value })} />
            </Field>
            <Field label="Cliente" htmlFor="inv-tenant">
              <Select id="inv-tenant" value={filters.tenantId} onChange={(event) => changeFilters({ tenantId: event.target.value })}>
                <option value="">Todos</option>
                {(tenants.data?.items ?? []).map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Exportación" htmlFor="inv-exported">
              <Select
                id="inv-exported"
                value={filters.exported}
                onChange={(event) => changeFilters({ exported: event.target.value as ErpInvoiceFilters["exported"] })}
              >
                <option value="all">Todas</option>
                <option value="pending">Pendientes de exportar</option>
                <option value="exported">Ya exportadas</option>
              </Select>
            </Field>
            <Field label="Facturas de prueba" htmlFor="inv-test">
              <Select
                id="inv-test"
                value={filters.test}
                onChange={(event) => changeFilters({ test: event.target.value as ErpInvoiceFilters["test"] })}
              >
                <option value="include">Mostrar (con marca)</option>
                <option value="exclude">Ocultar</option>
                <option value="only">Solo las de prueba</option>
              </Select>
            </Field>
            <Button onClick={() => setFilters(DEFAULT_INVOICE_FILTERS)}>Quitar filtros</Button>
          </div>

          {filters.batchId ? (
            <div className="mt-3">
              <Notice>
                Mostrando las facturas de un lote concreto ({filters.batchId.slice(0, 8)}).{" "}
                <button type="button" className="underline" onClick={() => changeFilters({ batchId: "" })}>
                  Ver todas
                </button>
              </Notice>
            </div>
          ) : null}

          {invoices.isLoading ? <Spinner /> : null}
          {invoices.isError ? <ErrorNotice error={invoices.error} onRetry={() => void invoices.refetch()} /> : null}

          {invoices.data ? (
            <div className="mt-4" aria-live="polite">
              <p className="mb-2 text-sm text-ink-800 dark:text-ink-100">
                <strong>{invoices.data.total}</strong> factura(s) por <strong>{formatEuro(invoices.data.totalAmount)}</strong>
                {" · "}
                {describeInvoicePage(invoices.data.offset, invoices.data.invoices.length, invoices.data.total)}
              </p>
              {invoices.data.invoices.length === 0 ? (
                <p className="text-sm text-ink-500">
                  {filters.batchId
                    ? "Este lote ya no tiene facturas enlazadas: se borraron (por ejemplo, al quitar un plan de prueba) o se borró su enlace."
                    : "No hay facturas numeradas con esos filtros."}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="micro-label">
                        <th className="py-2 pr-4">Número</th>
                        <th className="py-2 pr-4">Fecha fiscal</th>
                        <th className="py-2 pr-4">Cliente</th>
                        <th className="py-2 pr-4 text-right">Neto</th>
                        <th className="py-2 pr-4 text-right">Impuesto</th>
                        <th className="py-2 pr-4 text-right">Total</th>
                        <th className="py-2 pr-4">Exportación</th>
                        <th className="py-2 pr-4">Referencia Stripe</th>
                        <th className="py-2">Rectificación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.data.invoices.map((invoice) => (
                        <tr key={invoice.id} className="border-t border-ink-200 dark:border-ink-800">
                          <td className="py-2 pr-4 whitespace-nowrap">
                            <span className="num font-medium">{invoice.reference}</span>
                            {invoice.kind === "RECTIFICATIVE" ? (
                              <>
                                {" "}
                                <Chip tone="warn">Rectificativa</Chip>
                              </>
                            ) : null}
                            {invoice.testMode ? (
                              <>
                                {" "}
                                <Chip tone="neutral">Prueba</Chip>
                              </>
                            ) : null}
                          </td>
                          <td className="py-2 pr-4 whitespace-nowrap">{invoice.fiscalDate ? formatDay(invoice.fiscalDate) : "—"}</td>
                          <td className="py-2 pr-4">{invoice.tenantName}</td>
                          <td className="py-2 pr-4 text-right num">{formatEuro(invoice.netAmount)}</td>
                          <td className="py-2 pr-4 text-right num">
                            {formatEuro(invoice.taxAmount)}
                            {invoice.taxPercent != null ? <span className="text-ink-500"> ({invoice.taxPercent} %)</span> : null}
                          </td>
                          <td className="py-2 pr-4 text-right num font-medium">{formatEuro(invoice.totalAmount)}</td>
                          <td className="py-2 pr-4 whitespace-nowrap">
                            {invoice.exported ? (
                              <Chip tone="ok">Exportada{invoice.exportedAt ? ` ${formatDay(invoice.exportedAt)}` : ""}</Chip>
                            ) : (
                              <Chip tone="warn">Pendiente</Chip>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-ink-500 dark:text-ink-400">
                            {invoice.kind === "RECTIFICATIVE" ? "—" : (invoice.providerNumber ?? invoice.providerInvoiceId ?? "—")}
                          </td>
                          <td className="py-2 whitespace-nowrap">
                            <Button variant="quiet" disabled={invoicePdf.isPending} onClick={() => void handleOpenPdf(invoice)}>
                              Factura fiscal
                            </Button>{" "}
                            {invoice.kind === "RECTIFICATIVE" ? (
                              <span className="text-xs text-ink-500 dark:text-ink-400">Rectifica a {invoice.rectifiesReference ?? "—"}</span>
                            ) : invoice.rectifiedByReference ? (
                              <Chip tone="danger">Rectificada por {invoice.rectifiedByReference}</Chip>
                            ) : (
                              <Button
                                variant="quiet"
                                disabled={rectify.isPending}
                                onClick={() => void handleRectify(invoice)}
                              >
                                Rectificar
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(() => {
                const { prev, next } = neighbourOffsets(invoices.data.offset, invoices.data.limit, invoices.data.total);
                if (prev === null && next === null) return null;
                return (
                  <div className="mt-3 flex gap-2">
                    <Button disabled={prev === null} onClick={() => setFilters((current) => ({ ...current, offset: prev ?? 0 }))}>
                      Anterior
                    </Button>
                    <Button disabled={next === null} onClick={() => setFilters((current) => ({ ...current, offset: next ?? 0 }))}>
                      Siguiente
                    </Button>
                  </div>
                );
              })()}
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
